import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useIntakeAssessmentList,
  INTAKE_ASSESSMENT_KIND_LABELS,
  INTAKE_ASSESSMENT_STATUS_LABELS,
  INTAKE_ASSESSMENT_RECORD_KINDS,
  INTAKE_ASSESSMENT_STATUSES,
  type IntakeAssessmentRecordKind,
  type IntakeAssessmentStatus,
} from '@/hooks/useIntakeAssessment';
import {
  INTAKE_ASSESSMENT_PERMISSIONS,
  canSeeIntakeAssessmentMenu,
  hasPermission,
} from '@/lib/intakeAssessmentPermissions';

const STATUS_BADGE_COLORS: Record<IntakeAssessmentStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  changes_requested: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-zinc-200 text-zinc-700',
};

export default function IntakeAssessmentListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { rows, total, page, limit, loading, error, fetchRecords } =
    useIntakeAssessmentList();

  const [filterKind, setFilterKind] = useState<IntakeAssessmentRecordKind | ''>(
    '',
  );
  const [filterStatus, setFilterStatus] = useState<IntakeAssessmentStatus | ''>(
    '',
  );
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [pageInput, setPageInput] = useState(1);

  const canRead = canSeeIntakeAssessmentMenu(permissions);
  const canCreate = hasPermission(
    permissions,
    INTAKE_ASSESSMENT_PERMISSIONS.CREATE,
  );

  useEffect(() => {
    if (!canRead) return;
    void fetchRecords({
      recordKind: filterKind || undefined,
      status: filterStatus || undefined,
      customerId: filterCustomerId || undefined,
      search: search || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [
    filterKind,
    filterStatus,
    filterCustomerId,
    search,
    pageInput,
    canRead,
    fetchRecords,
  ]);

  if (!canRead) {
    return <AccessDenied resource="接诊评估" />;
  }

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">接诊评估</h1>
          <p className="text-sm text-muted-foreground">
            黄金分析表 / 成交意向表 / 接诊评估的草稿、提交与审核记录。
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate('/intake-assessment/records/create')}>
            新建记录
          </Button>
        )}
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="filter-kind">记录类型</Label>
            <select
              id="filter-kind"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={filterKind}
              onChange={(e) =>
                setFilterKind(
                  (e.target.value as IntakeAssessmentRecordKind | '') || '',
                )
              }
            >
              <option value="">全部</option>
              {INTAKE_ASSESSMENT_RECORD_KINDS.map((k) => (
                <option key={k} value={k}>
                  {INTAKE_ASSESSMENT_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-status">状态</Label>
            <select
              id="filter-status"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(
                  (e.target.value as IntakeAssessmentStatus | '') || '',
                )
              }
            >
              <option value="">全部</option>
              {INTAKE_ASSESSMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {INTAKE_ASSESSMENT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-customer">客户 ID</Label>
            <Input
              id="filter-customer"
              value={filterCustomerId}
              onChange={(e) => setFilterCustomerId(e.target.value)}
              placeholder="按客户 ID 过滤"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-search">标题搜索</Label>
            <Input
              id="filter-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="按标题过滤"
            />
          </div>
        </div>
      </Card>

      {error && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          加载失败：{error.message}
        </Card>
      )}

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">类型</th>
              <th className="px-3 py-2 text-left font-medium">标题</th>
              <th className="px-3 py-2 text-left font-medium">客户 ID</th>
              <th className="px-3 py-2 text-left font-medium">负责人</th>
              <th className="px-3 py-2 text-left font-medium">状态</th>
              <th className="px-3 py-2 text-left font-medium">提交时间</th>
              <th className="px-3 py-2 text-left font-medium">审核时间</th>
              <th className="px-3 py-2 text-left font-medium">更新时间</th>
              <th className="px-3 py-2 text-left font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  加载中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  暂无记录
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-muted/30">
                  <td className="px-3 py-2">
                    {INTAKE_ASSESSMENT_KIND_LABELS[row.recordKind]}
                  </td>
                  <td className="px-3 py-2 font-medium">{row.title}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.customerId}</td>
                  <td className="px-3 py-2 text-xs">{row.ownerUserId}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${STATUS_BADGE_COLORS[row.status]}`}
                    >
                      {INTAKE_ASSESSMENT_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {row.submittedAt
                      ? new Date(row.submittedAt).toLocaleString('zh-CN')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {row.reviewedAt
                      ? new Date(row.reviewedAt).toLocaleString('zh-CN')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {new Date(row.updatedAt).toLocaleString('zh-CN')}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() =>
                        navigate(`/intake-assessment/records/${row.id}`)
                      }
                    >
                      详情
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          共 {total} 条 · 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPageInput((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </Button>
        </div>
      </div>
    </div>
  );
}
